import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { PageHeader, Field, SimpleSelect } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, FileText, Loader2 } from "lucide-react";

export default function Export() {
  const { indikators, tahuns } = useMeta();
  const [indikator, setIndikator] = useState("");
  const [tahun, setTahun] = useState("");
  const [loading, setLoading] = useState("");

  useEffect(() => { if (!indikator && indikators.length) setIndikator(indikators[0].kode); }, [indikators]);
  useEffect(() => { if (!tahun && tahuns.length) setTahun(String(tahuns[0])); }, [tahuns]);

  const download = async (type) => {
    setLoading(type);
    try {
      const res = await api.get(`/export/${type}?indikator=${encodeURIComponent(indikator)}&tahun=${tahun}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Laporan_${indikator}_${tahun}.${type === "excel" ? "xlsx" : "pdf"}`.replace(/ /g, "_");
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Laporan ${type.toUpperCase()} berhasil diunduh`);
    } catch (err) {
      toast.error("Gagal membuat laporan");
    } finally {
      setLoading("");
    }
  };

  return (
    <div data-testid="export-page">
      <PageHeader icon={FileDown} title="Export Laporan Bulanan" subtitle="Hasilkan laporan siap kirim ke pimpinan: ringkasan per departemen, tren per bulan, dan status capaian." />

      <Card className="border-slate-200 shadow-sm max-w-3xl">
        <CardHeader><CardTitle className="text-lg">Konfigurasi Laporan</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap gap-4">
            <Field label="Indikator">
              <SimpleSelect testId="export-indikator-select" value={indikator} onChange={setIndikator}
                options={indikators.map((i) => ({ label: i.kode, value: i.kode }))} className="w-[240px]" />
            </Field>
            <Field label="Tahun">
              <SimpleSelect testId="export-tahun-select" value={tahun} onChange={setTahun}
                options={tahuns.map((t) => ({ label: String(t), value: t }))} className="w-[140px]" />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button onClick={() => download("excel")} disabled={loading} data-testid="rekap-export-excel-button"
              className="group border-2 border-slate-200 rounded-xl p-6 text-left hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors disabled:opacity-60">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 rounded-lg bg-emerald-600 flex items-center justify-center text-white">
                  {loading === "excel" ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileSpreadsheet className="w-5 h-5" />}
                </div>
                <div className="font-semibold text-slate-900">Export Excel (.xlsx)</div>
              </div>
              <p className="text-sm text-slate-500">3 sheet: Ringkasan Bulan, Rekap Departemen, dan matriks Rekap Bulan dengan warna status.</p>
            </button>

            <button onClick={() => download("pdf")} disabled={loading} data-testid="rekap-export-pdf-button"
              className="group border-2 border-slate-200 rounded-xl p-6 text-left hover:border-rose-400 hover:bg-rose-50/40 transition-colors disabled:opacity-60">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 rounded-lg bg-rose-600 flex items-center justify-center text-white">
                  {loading === "pdf" ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                </div>
                <div className="font-semibold text-slate-900">Export PDF</div>
              </div>
              <p className="text-sm text-slate-500">Ringkasan keseluruhan & rekap departemen dengan status warna, siap presentasi.</p>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
